import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, CheckCircle, XCircle, Loader2, Plug } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { SettingsCard } from '@/components/settings/ui/SettingsCard';

interface DotyposIntegrationProps {
  businessId: string;
}

const DotyposIntegration: React.FC<DotyposIntegrationProps> = ({ businessId }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [cloudId, setCloudId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const dotyposCardRef = React.useRef<HTMLDivElement>(null);

  // Dotypos OAuth configuration
  const DOTYPOS_CLIENT_ID = 'tag-inn';
  const DOTYPOS_CLIENT_SECRET = 'um6MZ2by2wFa38njfj8e';
  const PASS_GENERATOR_URL = import.meta.env.VITE_PASS_GENERATOR_URL || 'https://pass.taginn.app';
  const REDIRECT_URI = `${PASS_GENERATOR_URL}/dotypos/callback`;

  useEffect(() => {
    checkDotyposConnection();
  }, [businessId]);

  // Effect to handle Dotypos highlighting from LoyaltyPassBuilder
  useEffect(() => {
    const shouldHighlight = sessionStorage.getItem('highlightDotypos');

    // Only proceed if we should highlight AND loading is complete
    if (shouldHighlight === 'true' && !loading && dotyposCardRef.current) {
      // Clear the flag
      sessionStorage.removeItem('highlightDotypos');

      // Small delay to ensure DOM is fully painted
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (dotyposCardRef.current) {
            // Scroll to the Dotypos card
            dotyposCardRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });

            // Add highlight classes
            dotyposCardRef.current.classList.add(
              'ring-2',
              'ring-blue-500',
              'ring-offset-2',
              'transition-all',
              'duration-300'
            );

            // Remove highlight after 3 seconds
            setTimeout(() => {
              if (dotyposCardRef.current) {
                dotyposCardRef.current.classList.remove(
                  'ring-2',
                  'ring-blue-500',
                  'ring-offset-2',
                  'transition-all',
                  'duration-300'
                );
              }
            }, 3000);
          }
        }, 100);
      });
    }
  }, [loading]); // Trigger when loading changes

  const checkDotyposConnection = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('businesses')
        .select('dotypos_enabled, dotypos_cloud_id')
        .eq('id', businessId)
        .single();

      if (error) {
        console.error('Error checking Dotypos connection:', error);
        return;
      }

      setIsConnected(data?.dotypos_enabled || false);
      setCloudId(data?.dotypos_cloud_id || null);
    } catch (error) {
      console.error('Error checking Dotypos connection:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectToDotypos = () => {
    // Build Dotypos authorization URL
    const authUrl = new URL('https://admin.dotykacka.cz/client/connect');
    authUrl.searchParams.append('client_id', DOTYPOS_CLIENT_ID);
    authUrl.searchParams.append('client_secret', DOTYPOS_CLIENT_SECRET);
    authUrl.searchParams.append('scope', '*');
    authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.append('state', businessId); // Pass business ID in state

    // Open in new tab
    window.open(authUrl.toString(), '_blank');
  };

  const handleDisconnect = async () => {
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          dotypos_enabled: false,
          dotypos_refresh_token: null,
          dotypos_cloud_id: null
        })
        .eq('id', businessId);

      if (error) throw error;

      setIsConnected(false);
      setCloudId(null);
      toast.success('Dotypos disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting Dotypos:', error);
      toast.error('Failed to disconnect Dotypos');
    }
  };

  const connectionBadge = isConnected ? (
    <Badge variant="default" className="flex items-center gap-1">
      <CheckCircle className="h-3 w-3" />
      Connected
    </Badge>
  ) : (
    <Badge className="flex items-center gap-1">
      <XCircle className="h-3 w-3" />
      Not Connected
    </Badge>
  );

  if (loading) {
    return (
      <SettingsCard
        icon={Plug}
        title="Dotypos POS Integration"
        description="Connect your Dotypos POS system"
      >
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      ref={dotyposCardRef}
      icon={Plug}
      title="Dotypos POS Integration"
      description="Connect your Dotypos POS to automatically sync loyalty customers"
      headerAction={connectionBadge}
    >
      <div className="space-y-4">
        {!isConnected ? (
          <>
            <p className="text-sm text-gray-600">
              Connect your Dotypos POS system to enable automatic customer syncing for Dotypos Points loyalty programs.
              When customers add a loyalty pass, they'll automatically be created in your Dotypos system.
            </p>

            <div className="space-y-2">
              <h4 className="font-medium text-sm">Benefits:</h4>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Automatic customer registration in Dotypos</li>
                <li>Seamless loyalty point tracking</li>
                <li>Unified customer database</li>
                <li>Real-time synchronization</li>
              </ul>
            </div>

            <Button
              onClick={handleConnectToDotypos}
              className="w-full"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Connect to Dotypos
            </Button>

            <p className="text-xs text-gray-500 text-center">
              You'll be redirected to Dotypos to authorize the connection
            </p>
          </>
        ) : (
          <>
            <Alert>
              <AlertDescription className="flex items-center justify-between">
                <span>
                  ✅ Your Dotypos POS is connected
                  {cloudId && <span className="ml-2 text-xs text-gray-500">(Cloud ID: {cloudId})</span>}
                </span>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <h4 className="font-medium text-sm">Active Features:</h4>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>✓ Automatic customer syncing enabled</li>
                <li>✓ Dotypos Points programs available</li>
                <li>✓ Real-time point tracking</li>
              </ul>
            </div>

            <Button
              onClick={handleDisconnect}
              variant="outline"
              className="w-full"
            >
              Disconnect Dotypos
            </Button>
          </>
        )}
      </div>
    </SettingsCard>
  );
};

export default DotyposIntegration;
